/* eslint-disable camelcase */
import { observer } from "mobx-react-lite"
import React, { FC, useCallback, useEffect, useState } from "react"
import {
  Appearance,
  useWindowDimensions,
  ViewStyle
} from "react-native"
import {
  Button,
  FlatList, ListEmpty,
  ListRenderItem,
  ListRenderItemInfo,
  Screen,
  Text,
} from "../../../../components"
import numeral from "numeral"
import { useStores } from "../../../../models"
import { BatchStoreModel } from "../../../../models/app-store/farm-store/batch-store/batch-store"
import { FarmStoreModel } from "../../../../models/app-store/farm-store/farm-store"
import {
  color,
  nativeBaseTheme,
} from "../../../../theme"
import { SafeAreaView } from "react-native-safe-area-context"
import { View } from "native-base"
import _ from "lodash"
import { translate } from "../../../../i18n"
import { navigate } from "../../../../navigators"
import { useFocusEffect } from "@react-navigation/native"
import JalaAnalytic from "../../../../utils/jala-analytic"
import { ShrimpPrice } from "../../../../database"
import { InfoWarningModal } from "../overview-feed-cost-list/overview-feed-cost-list-screen"
import { getSnapshot } from "mobx-state-tree"

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  flex: 1,
}

const HEADER: ViewStyle = {
  borderTopWidth: 1,
  borderBottomWidth: 1,
  borderColor: color.palette.gray200,
  flexDirection: 'row',
  paddingVertical: 8,
  alignItems: 'center'
}

const SETTING_BUTTON: ViewStyle = {
  marginBottom: 4
}

const CONTENT_ITEM: ViewStyle = {
  borderBottomWidth: 1,
  borderColor: color.palette.gray200,
  paddingVertical: 10,
  flexDirection: 'row'
}

const Header: FC<{ colorScheme?: "light"|"dark" }> = observer(function Header(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;
  const { appStore: { farmStore }, shrimpPriceStore } = useStores()
  const { activeBatchStore, lastFarmShrimpPrice } = farmStore || FarmStoreModel.create({})
  const { predictionSellingPrice, cycles, isFeedActualWithin4Days, loadingCyclePrediction } = activeBatchStore || BatchStoreModel.create({})

  const [ farmShrimpPrice, setModelFarmShrimpPrice ] = useState<any>(null)

  useEffect(() => {
    if (!lastFarmShrimpPrice?.shrimp_price_id) return;
    const shrimpPrice = shrimpPriceStore.shrimpPrices.find((s) => s.id === lastFarmShrimpPrice?.shrimp_price_id);
    (shrimpPrice
      ? Promise.resolve([ShrimpPrice.hydrate({...shrimpPrice})])
      : ShrimpPrice.fetch({ id__eq: lastFarmShrimpPrice.shrimp_price_id })
    ).then((shrimpPrices: Array<ShrimpPrice>) => {
      const shrimpPrice = shrimpPrices[0]
      setModelFarmShrimpPrice(shrimpPrice)
    }).catch()
  }, [lastFarmShrimpPrice?.shrimp_price_id, shrimpPriceStore, activeBatchStore])

  const onPressSetting = useCallback(() => {
    const params = farmShrimpPrice ? farmShrimpPrice.toArray() : null
    navigate('Home.Stack', 'Home.FarmShrimpPrice.Create', params)
  }, [farmShrimpPrice])

  const source = farmShrimpPrice ? (farmShrimpPrice.private ? translate("farmShrimpPriceForm.private") : translate("farmShrimpPriceForm.newest")) : "-"

  return (
    <View paddingX="16px" paddingTop="8px" paddingBottom="15px">
      <View flexDirection="row" justifyContent="space-between">
        <View flex={1} paddingRight="8px">
          <Text text={farmStore.farm.name} preset="title3" marginBottom="4px" colorScheme={colorScheme} isTruncated numberOfLines={1} />
        </View>
        <Button
          testID="jala:id/setting-button"
          colorScheme={colorScheme}
          tx="overviewScreen.setting"
          preset="link"
          textPreset="title3"
          style={SETTING_BUTTON}
          onPress={onPressSetting}
        />
      </View>
      <Text tx="overviewSellingPricePredictionListScreen.subtitle" preset="body3" colorScheme={colorScheme} color={nativeBaseTheme.colors.gray[colorScheme === 'light' ? '700' : '300']} />
      <View flexDirection="row" alignItems="center">
        <Text
          text={numeral(predictionSellingPrice / 1000000).format('0,0.[00]')}
          preset="display1"
          marginRight="3px"
          colorScheme={colorScheme}
          color={loadingCyclePrediction && color.palette.dust500 || undefined}
        />
        <Text tx="million" preset="display2" marginRight="8px" colorScheme={colorScheme} color={color.palette.dust500} />
      </View>
      <Text text={translate("overviewScreen.fromActivePonds", { count: cycles.length })} preset="body3" colorScheme={colorScheme} color={color.palette.dust500} />
      {!isFeedActualWithin4Days && <InfoWarningModal />}
      <View marginTop="6px">
        <Text colorScheme={colorScheme}>
          <Text
            tx="overviewSellingPricePredictionListScreen.source"
            preset="body3"
            color={colorScheme === "light" ? color.palette.gray800 : color.palette.gray200}
          />
          <Text
            text={source}
            preset="title5"
            color={colorScheme === "light" ? color.palette.gray800 : color.palette.gray200}
            width="70%"
            marginLeft="4px"
            numberOfLines={1}
          />
        </Text>
      </View>
    </View>
  )
})

const TableHeader: FC<{ colorScheme?: "light"|"dark" }> = (props) => {
  const { colorScheme } = props;

  return (
    <View style={HEADER}>
      <View width="10%" paddingX="8px" alignItems="flex-start">
        <Text text="#" preset="body3" transformText={_.upperFirst} colorScheme={colorScheme} />
      </View>
      <View width="30%" paddingX="8px" alignItems="flex-start">
        <Text tx="cyclePredictions.pond" transformText={_.upperFirst} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="12.5%" paddingX="8px" alignItems="flex-start">
        <Text tx="cyclePredictions.age" transformText={_.upperFirst} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="12.5%" paddingX="4px" alignItems="flex-start">
        <Text tx="cyclePredictions.size" transformText={_.upperFirst} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="35%" paddingX="4px" alignItems="flex-start">
        <Text tx="cyclePredictions.selling_price" transformText={_.upperFirst} preset="body3" colorScheme={colorScheme} />
      </View>
    </View>
  )
}

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any> }> = observer(function Item(props) {
  const { colorScheme, info } = props
  const { index, item: { age, pond_name, cycle_predictions } } = info;
  const { size, selling_price, shrimp_price_currency } = cycle_predictions.find((c) => c.age <= age) || {};

  const sizeText = size >= 1000 ? `> ${numeral(size).format('0,0.[0]a')}` : numeral(size).format('0,0');

  return (
    <View style={CONTENT_ITEM}>
      <View width="10%" paddingX="8px" alignItems="flex-start">
        <Text text={`${index + 1}`} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="30%" paddingX="8px" alignItems="flex-start">
        <Text text={pond_name} preset="body3" numberOfLines={1} isTruncated colorScheme={colorScheme} />
      </View>
      <View width="12.5%" paddingX="8px" alignItems="flex-start">
        <Text text={`${age}`} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="13.5%" paddingX="4px" alignItems="flex-start">
        <Text text={sizeText} preset="body3" colorScheme={colorScheme} numberOfLines={1} isTruncated />
      </View>
      <View width="34%" paddingX="4px" alignItems="flex-start">
        <Text text={`${shrimp_price_currency ? `${shrimp_price_currency} ` : ''}${numeral(selling_price).format('0,0')}`} preset="body3" colorScheme={colorScheme} />
      </View>
    </View>
  )
});

export const OverviewSellingPricePredictionListScreen: FC<{ colorScheme?: "light" | "dark" }> = observer(function OverviewSurvivalRatePredictionList(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;
  const { height } = useWindowDimensions();

  // Pull in one of our MST stores
  const {
    appStore: { farmStore }
  } = useStores();
  const { activeBatchStore } = farmStore || FarmStoreModel.create({})
  const { cycles, loadingCyclePrediction } = activeBatchStore || BatchStoreModel.create({})

  const keyExtractor = useCallback((item, index) => `${item.id || item.started_at || index}`, [])

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    return <Item info={info} colorScheme={colorScheme} />
  }, [colorScheme])

  const onRefresh = useCallback(() => {
    activeBatchStore.init()
  }, [activeBatchStore])

  useFocusEffect(useCallback(() => {
    farmStore.getFarmShrimpPrices()
    JalaAnalytic.log('Mobile app: home - overview - prediksi jual');
  }, [farmStore]))

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      hideOnBlurred={false}
    >
      <FlatList
        data={getSnapshot(cycles)}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        ListHeaderComponent={<>
          <Header colorScheme={colorScheme} />
          <TableHeader colorScheme={colorScheme} />
        </>}
        ListEmptyComponent={<ListEmpty colorScheme={colorScheme} height={`${height - 270}px`} />}
        ListFooterComponent={<SafeAreaView edges={['bottom']} />}
        onRefresh={onRefresh}
        refreshing={!!loadingCyclePrediction}
        estimatedItemSize={37}
      />
    </Screen>
  )
})
